import CircleBlank from '@doltech/ui/lib/figma/BlankEditor/ViewTest/CircleBlank';
import { QuestionCircle, QuestionCircleType } from '@doltech/ui/lib/figma/CircleText/CircleText';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { SubText } from '@doltech/ui/lib/figma/Typography';
import { useDeviceDetect } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { numberToABC, numberToRoman } from '@doltech/utils/lib/text';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

export const CircleTextType = {
  NUMBER: "string",
  ALPHABET: "alphabet",
  ROMAN: "roman"
} as const;

export type CircleTextType = typeof CircleTextType[keyof typeof CircleTextType];

export interface ViewTestMatchingProps {
  currentQuestionNo: number;
  title?: string;
  findingOptions?: any[];
  questionOptions: any[];
  findingOptionCircleTextType?: string;
  questionOptionCircleTextType?: string;
  isBlankAtEnd?: boolean;
  isPdfContent?: boolean;
}

const buildCircleText = (num, type) => {
  if (CircleTextType.ALPHABET === type) {
    return numberToABC(num);
  }
  if (CircleTextType.ROMAN === type) {
    return numberToRoman(num);
  }
  return num;
};

export const ViewTestMatching = observer((props: ViewTestMatchingProps) => {
  const {
    currentQuestionNo,
    title,
    findingOptions,
    questionOptions,
    findingOptionCircleTextType = CircleTextType.ALPHABET,
    questionOptionCircleTextType = CircleTextType.NUMBER,
    isBlankAtEnd = false,
    isPdfContent,
    ...rest
  } = props;
  const { isMobile = false } = useDeviceDetect();

  return (
    <MatchingQuestion className="dol-question" {...rest} isMobile={isMobile && !isPdfContent}>
      {findingOptions && (
        <FindingBox>
          {title && <Title>{title}</Title>}
          <FindingList>
            {findingOptions.map((option, idx) => {
              return (
                <FindingItem key={option.key}>
                  <QuestionCircle className={cl(QuestionCircleType.GRAY_40, 'option-circle')}>
                    <SubText>{buildCircleText(idx + 1, findingOptionCircleTextType)}</SubText>
                  </QuestionCircle>
                  <span>{option.value}</span>
                </FindingItem>
              );
            })}
          </FindingList>
        </FindingBox>
      )}
      <QuestionList>
        {questionOptions.map((option, idx) => {
          return (
            <Question key={option.key}>
              {isBlankAtEnd ? (
                <>
                  {option.value}
                  <CircleBlank
                    circleText={buildCircleText(
                      currentQuestionNo + idx,
                      questionOptionCircleTextType
                    )}
                  />
                </>
              ) : (
                <>
                  <CircleBlank
                    circleText={buildCircleText(
                      currentQuestionNo + idx,
                      questionOptionCircleTextType
                    )}
                  />
                  {option.value}
                </>
              )}
            </Question>
          );
        })}
      </QuestionList>
    </MatchingQuestion>
  );
});

const MatchingQuestion = styled.div<{ isMobile: boolean }>`
  font-size: 16px;
  padding: ${(p) => (p.isMobile ? '16px' : '24px')};
`;

const FindingBox = styled.div`
  background: ${colorsV2.gray10};
  border-radius: 8px;
  padding: 16px;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
`;

const FindingList = styled.div`
  margin-top: 16px;
`;

const FindingItem = styled.div`
  min-height: 40px;
  padding: 8px 0;
  line-height: 24px;
  display: flex;

  &:last-child {
    min-height: 32px;
    padding-bottom: 0;
  }

  > .option-circle {
    min-width: 24px;
    margin-right: 8px;
  }
`;

const QuestionList = styled.div`
  margin-top: 20px;
`;

const Question = styled.div`
  display: flex;
  min-height: 40px;
  padding: 8px 0;
  line-height: 24px;

  &:last-child {
    min-height: 32px;
    padding-bottom: 0;
  }

  > .option-circle {
    min-width: 24px;
    margin-right: 8px;
  }
`;
